import { AnalyticsInfo, FrontendTrackedEvent, User, PrivacyProperties } from 'frontend/types';
import { snakeToCamel } from 'frontend/utils/textFormatters';
import { safeWindow } from 'frontend/utils/window';
import { getEnvironmentForErrorReporting } from './environment';

export function initAnalytics(store: any, signOfLifeOnly = false) {
  // we need to dynamically import '@/util/analytics' because segmentio references window a lot
  import('@/util/analytics').then(tracking => {
    const initWhenDataAvailable = () => {
      const analyticsData: AnalyticsInfo = store.state.globals.analytics;
      const user: User = store.state.globals.user;
      const privacyProperties: PrivacyProperties = store.state.globals.privacy_properties;
      if (analyticsData && user && privacyProperties) {
        const isHomeRoute = store.state.route?.name === 'userhome';
        // load Braze only on the homepage, and only if the user has been signed up for at least a day to limit MAUs
        if (isHomeRoute && (user.days_since_signup || 0) >= 1) {
          const env = getEnvironmentForErrorReporting();

          tracking.initBraze(analyticsData.analytics_identity, env?.environment);
        }

        loadGoogleTagManagerScript(
          analyticsData.google_tag_manager_id,
          privacyProperties.third_party_cookies_disabled,
          analyticsData.analytics_identity,
        );
        tracking.remoteInit(analyticsData, user);
        if (!signOfLifeOnly) {
          const formattedAnalyticsData: any = {};
          Object.entries(analyticsData).forEach(([key, value]) => {
            formattedAnalyticsData[snakeToCamel(key)] = value;
          });
          tracking.initTrackingFromData(
            formattedAnalyticsData,
            !privacyProperties.third_party_cookies_disabled,
          );
        }

        analyticsData.javascript_tracked_events.forEach((event: FrontendTrackedEvent) => {
          tracking.track(event.event_name, event.event_properties);
        });

        store.dispatch('globals/clearTrackedEvents');
      } else {
        setTimeout(initWhenDataAvailable, 300);
      }
    };
    initWhenDataAvailable();
  });
}

type TrackingDestination = 'Amplitude' | 'Segment';

interface TrackingOptions {
  exclude: TrackingDestination[];
}

export function track(
  eventName: string,
  propertyData: any,
  options: Partial<TrackingOptions> = {},
) {
  // Don't track anything on the server
  if (process.env.VUE_ENV !== 'server') {
    import('@/util/analytics').then(tracking => {
      tracking.track(eventName, propertyData, options);
    });
  }
}

export function googlePixelTrackStartTrial(store: any) {
  const analyticsData: AnalyticsInfo = store.state.globals.analytics;
  if (analyticsData) {
    trackGooglePixel(
      analyticsData.google_ad_pixel_id,
      { google_conversion_label: 'ryunCIHCq7YBEJXEvuAD' }, // StartTrial (web)
    );
  }
}

function trackGooglePixel(
  googleAdPixelId: string,
  additionalProperties: { [key: string]: string },
) {
  if (process.env.VUE_ENV !== 'server') {
    const handleTrack = () => {
      (window as any).google_trackConversion({
        google_conversion_id: googleAdPixelId,
        google_conversion_language: 'en',
        google_conversion_format: '3',
        google_conversion_color: 'ffffff',
        ...additionalProperties,
      });
    };

    if ((window as any).google_trackConversion) {
      handleTrack();
    } else {
      const script = document.createElement('script');
      script.src = 'https://www.googleadservices.com/pagead/conversion_async.js';
      script.async = true;
      script.addEventListener('load', handleTrack);
      document.body.appendChild(script);
    }
  }
}

interface GoogleTagManagerData {
  [index: string]: string;
}

export function trackGoogleTagManager(data: GoogleTagManagerData) {
  const browserWindow = safeWindow() as any;
  if (browserWindow) {
    browserWindow.dataLayer = browserWindow.dataLayer || [];

    browserWindow.dataLayer.push({ ...data });
  }
}

export function loadGoogleTagManagerScript(
  gtmId: string,
  thirdPartyCookiesDisabled: boolean,
  userIdentity: string,
): void {
  const browserWindow = safeWindow() as any;

  if (browserWindow) {
    browserWindow.dataLayer = browserWindow.dataLayer || [];

    const initialGtmDataVariables = {
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
      third_party_cookies_disabled: thirdPartyCookiesDisabled ? 'true' : 'false',
      identity: userIdentity,
    };

    browserWindow.dataLayer.unshift(initialGtmDataVariables);

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    script.async = true;
    document.body.appendChild(script);
  }
}

export function quantcastTrack(eventName: string, planInterval?: string) {
  import('@/util/analytics').then(tracking => {
    tracking.quantcastTrack(eventName, planInterval);
  });
}

export function tatariTrack(eventName: string, eventData?: { [key: string]: any }) {
  import('@/util/analytics').then(tracking => {
    tracking.tatariTrack(eventName, eventData);
  });
}

function facebookPixelWasLoaded(): boolean {
  const browserWindow = safeWindow();
  return !!browserWindow && (browserWindow as any).fbq;
}

export function addToCartTrack(): void {
  if (facebookPixelWasLoaded()) {
    (safeWindow() as any).fbq('track', 'AddToCart');
  }
}
